import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Copy, KeyRound, LogOut, RefreshCw, ShieldCheck, UserCheck, UserX } from "lucide-react";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [newCode, setNewCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [codesRes, usersRes] = await Promise.all([
        api.get("/admin/invite-codes"),
        api.get("/admin/users"),
      ]);
      setCodes(codesRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      toast.error(apiError(e, "Não foi possível carregar o painel."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCode = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invite-codes", { note: note.trim() || null });
      setNewCode(data.code);
      setNote("");
      await load();
      toast.success("Código criado!");
    } catch (e) {
      toast.error(apiError(e, "Não foi possível criar o código."));
    } finally {
      setCreating(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(newCode);
      toast.success("Código copiado!");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const revokeCode = async (id) => {
    if (!window.confirm("Revogar este código?")) return;
    try {
      await api.post(`/admin/invite-codes/${id}/revoke`);
      await load();
      toast.success("Código revogado.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setAccess = async (u, status) => {
    const label = status === "blocked" ? "bloquear" : "reativar";
    if (!window.confirm(`Deseja ${label} o acesso de ${u.name || u.email}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/access`, { status });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, access_status: status } : x));
      toast.success(status === "blocked" ? "Acesso bloqueado." : "Acesso reativado.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F6]">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#682F3E]" />
              <h1 className="font-display text-xl font-bold text-[#27272A]">Admin NailControl</h1>
            </div>
            <p className="text-xs text-[#71717A] mt-1">{user?.email}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-2 rounded-xl border border-[#E4E4E7] text-sm text-[#71717A]">
              <RefreshCw size={16} />
            </button>
            <button onClick={handleLogout} className="px-3 py-2 rounded-xl border border-[#E4E4E7] text-sm text-[#71717A] flex items-center gap-1">
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <section className="card-nc p-5">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={18} className="text-[#682F3E]" />
            <h2 className="font-display text-lg font-bold text-[#27272A]">Gerar acesso de cliente</h2>
          </div>
          <p className="text-sm text-[#71717A] mb-4">
            Gere um código depois da compra. Cada código só pode criar uma conta.
          </p>

          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Identificação opcional: nome, pedido ou e-mail"
              maxLength={160}
              className="input-nc flex-1"
            />
            <button onClick={createCode} disabled={creating} className="btn-primary-nc md:w-auto">
              {creating ? "Gerando…" : "Gerar código"}
            </button>
          </div>

          {newCode && (
            <div className="mt-4 rounded-2xl border-2 border-[#682F3E] bg-[#F5ECE8] p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-[#682F3E]">Código novo — copie agora</div>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 text-xl md:text-2xl font-bold tracking-wider text-[#27272A] break-all">{newCode}</code>
                <button onClick={copyCode} className="btn-primary-nc px-3 py-2">
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs text-[#71717A] mt-2">
                Por segurança, o código completo só aparece nesta criação. Depois o painel mostra apenas uma versão mascarada.
              </p>
            </div>
          )}
        </section>

        <section className="card-nc p-5">
          <h2 className="font-display text-lg font-bold text-[#27272A] mb-4">Códigos de acesso</h2>
          {loading ? (
            <p className="text-sm text-[#71717A]">Carregando…</p>
          ) : codes.length === 0 ? (
            <p className="text-sm text-[#71717A]">Nenhum código criado ainda.</p>
          ) : (
            <div className="space-y-2">
              {codes.map(code => {
                const status = code.revoked ? "Revogado" : code.used ? "Utilizado" : "Disponível";
                return (
                  <div key={code.id} className="border border-[#E4E4E7] rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-brand font-semibold text-sm text-[#27272A]">{code.code_preview}</div>
                      <div className="text-xs text-[#71717A] truncate">{code.note || "Sem identificação"}</div>
                      {code.used_by_email && <div className="text-xs text-[#71717A] mt-0.5">Usado por {code.used_by_email}</div>}
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      code.revoked ? "bg-[#FEE2E2] text-[#991B1B]" :
                      code.used ? "bg-[#EBF2EE] text-[#365F48]" :
                      "bg-[#F5ECE8] text-[#682F3E]"
                    }`}>{status}</span>
                    {!code.used && !code.revoked && (
                      <button onClick={() => revokeCode(code.id)} className="text-xs font-semibold text-[#B76E56] px-2 py-1">
                        Revogar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="card-nc p-5">
          <h2 className="font-display text-lg font-bold text-[#27272A] mb-1">Contas cadastradas</h2>
          <p className="text-sm text-[#71717A] mb-4">
            Bloquear uma conta impede login e acesso às rotas privadas, sem apagar os dados.
          </p>

          {loading ? (
            <p className="text-sm text-[#71717A]">Carregando…</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-[#71717A]">Nenhuma conta de cliente cadastrada.</p>
          ) : (
            <div className="space-y-2">
              {users.map(u => {
                const active = u.access_status !== "blocked";
                return (
                  <div key={u.id} className="border border-[#E4E4E7] rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#27272A] truncate">{u.display_name || u.name || "Sem nome"}</div>
                      <div className="text-xs text-[#71717A] truncate">{u.email}</div>
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${active ? "bg-[#EBF2EE] text-[#365F48]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                      {active ? "Ativa" : "Bloqueada"}
                    </span>
                    {active ? (
                      <button onClick={() => setAccess(u, "blocked")} className="inline-flex items-center gap-1 text-xs font-semibold text-[#B76E56] px-2 py-1">
                        <UserX size={14} /> Bloquear
                      </button>
                    ) : (
                      <button onClick={() => setAccess(u, "active")} className="inline-flex items-center gap-1 text-xs font-semibold text-[#365F48] px-2 py-1">
                        <UserCheck size={14} /> Reativar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
